import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Container,
  Fade,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Card,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Stack
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PageTitle from '../../../components/PageTitle';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import s3Utils from "../../../utils/s3Utils";

const API_URL = process.env.REACT_APP_DOT_NET_CORE_API_URL;


const NCMainReport = () => {
  const location = useLocation();
  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getLabTypeIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedLabTypeId = searchParams.get("labTypeId");
    if (encryptedLabTypeId) {
      try {
        const decryptedLabTypeId = decryptText(encryptedLabTypeId);
        console.log(
          "📍 Using decrypted labTypeId from URL query:",
          decryptedLabTypeId
        );
        return decryptedLabTypeId;
      } catch (error) {
        return encryptedLabTypeId;
      }
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const getLabTypeId = () => {
    const queryLabTypeId = getLabTypeIdFromQuery();
    if (queryLabTypeId) {
      return queryLabTypeId;
    }
    return null;
  };

  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [labTypeId, setLabTypeId] = useState(getLabTypeId());
  const [loading, setLoading] = useState(true); // Set to true to load data on mount
  const [reportData, setReportData] = useState(null); // Initialize with null to trigger loading
  const [assessorAllotmentData, setAssessorAllotmentData] = useState([]);
  const [mergedNCData, setMergedNCData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Define colors
  const colors = {
    primary: { main: '#1976d2' },
    error: { main: '#d32f2f' },
    success: { main: '#2e7d32' },
    grey: { 100: '#f5f5f5', 300: '#e0e0e0' }
  };

  // Fetch report data from Form217report API
  useEffect(() => {
    if (assessmentId && appId) {
      loadReportData();
    }
  }, [assessmentId, appId]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      // Fetch NC Main data
      const ncResponse = await axios.get(`${API_URL}/GetNCMainall`, {
        params: {
          assessmentId: assessmentId,
          appId: appId,
        }
      });

      const ncData = ncResponse.data;

      if (ncData.isstatus === false) {
        console.warn('NC API returned error:', ncData.message);
        showSnackbar('No NC data found', 'warning');
        setLoading(false);
        return;
      }

      // Fetch Assessor Allotment data
      const allotmentResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/do/assessor-allotment9`,
        {
          params: {
            mode: 64,
            appId: appId
          }
        }
      );

      const allotmentData = allotmentResponse.data;

      if (allotmentData.success) {
        setAssessorAllotmentData(allotmentData.data || []);

        // Merge NC data with assessor details
        const merged = (ncData.nc || []).map(nc => {
          // Find matching assessor details where allotedToId === assessorid
          const assessorDetails = allotmentData.data.find(
            assessor => assessor.allotedToId === nc.assessorid
          );

          return {
            ...nc,
            laboratoryName: assessorDetails?.laboratoryName || 'N/A',
            assessorName: assessorDetails?.assessorName || 'N/A',
            capacity: assessorDetails?.capacity || 'N/A',
            organisation: assessorDetails?.organisation || 'N/A',
            contactNumber: assessorDetails?.contactNumber || 'N/A',
            location: assessorDetails?.location || 'N/A',
            startDate: assessorDetails?.startDate || 'N/A',
            endDate: assessorDetails?.endDate || 'N/A',
            expertise: assessorDetails?.expertise || assessorDetails?.fieldOfExpertise || 'N/A'
          };
        });

        // Group NCs by assessor
        const groupedByAssessor = {};
        merged.forEach(nc => {
          if (!groupedByAssessor[nc.assessorid]) {
            groupedByAssessor[nc.assessorid] = [];
          }
          groupedByAssessor[nc.assessorid].push(nc);
        });

        setMergedNCData(merged);
        setReportData({ nc: ncData.nc, message: ncData.message, groupedByAssessor });
      } else {
        showSnackbar('Failed to load assessor allotment data', 'warning');
        setReportData({ nc: ncData.nc, message: ncData.message });
      }

    } catch (error) {
      console.error('Error loading report data:', error);
      showSnackbar('Error loading report data: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {

  };

  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle file opening with S3 utils
  const handleFileOpen = async (fileKey, fileName = 'document') => {
    try {
      const success = await s3Utils.handleFileViewWithS3(fileKey, {
        showError: (msg) => showSnackbar(msg, 'error'),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });

      if (!success) {
        showSnackbar(`Failed to open ${fileName}`, 'error');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      showSnackbar(`Failed to open ${fileName}: ${error.message}`, 'error');
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="primary">Loading Assessment Report...</Typography>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Container maxWidth="lg">
        <Box
          p={4}
          mt={4}
          textAlign="center"
          sx={{
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)',
            borderRadius: 2,
            border: '2px solid #f44336'
          }}
        >
          <AssignmentIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            No Report Data Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Unable to load assessment report. Please try again later.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ p: 2 }}>
      <Fade in={true} timeout={800}>
        <Box>
          {/* Header Section */}
          <Paper
            elevation={3}
            sx={{
              mb: 4,
              p: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <AssignmentIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Assessment Report - Non Conformities
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  National Accreditation Board for Testing and Calibration Laboratories
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />

            {/* Report Metadata */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Total NCs: ${mergedNCData.length}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </Paper>

          {/* Action Buttons */}
          {/* <Box sx={{ mb: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              size="medium"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: 600
              }}
            >
              Go Back
            </Button>
            <Button 
              variant="contained" 
              size="medium"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              Download Report
            </Button>
          </Box> */}

          {/* Report Sections - Separate Card for Each Assessor */}
          <Box sx={{ '& > *': { mb: 4 } }}>
            {reportData?.groupedByAssessor && Object.keys(reportData.groupedByAssessor).length > 0 ? (
              Object.entries(reportData.groupedByAssessor).map(([assessorId, ncs], assessorIndex) => (
                <Fade key={assessorId} in={true} timeout={1600 + assessorIndex * 200}>
                  <Card elevation={3} sx={{ mb: 4 }}>
                    {/* HEADER */}
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${assessorIndex % 2 === 0
                            ? '#42275a 0%, #734b6d 100%'
                            : '#1a237e 0%, #3949ab 100%'
                          })`,
                        color: "#fff",
                        textAlign: "center",
                        py: 2
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        ASSESSOR #{assessorIndex + 1} - NON-CONFORMITY SUMMARY
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        Total NCs: {ncs.length}
                      </Typography>
                    </Box>

                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableBody>
                          {/* ASSESSOR DETAILS HEADER
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Assessor Details (ID: {assessorId})
                              </Typography>
                            </TableCell>
                          </TableRow> */}

                          {/* ASSESSOR INFO ROW 1 */}
                          <TableRow>
                            <TableCell colSpan={2} sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="caption" color="text.secondary">Assessor Name</Typography>
                              <Typography variant="body2" fontWeight="medium">{ncs[0].assessorName}</Typography>
                            </TableCell>

                            <TableCell sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="caption" color="text.secondary">Capacity</Typography>
                              <Typography variant="body2" fontWeight="medium">{ncs[0].capacity}</Typography>
                            </TableCell>

                            <TableCell sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="caption" color="text.secondary">Organisation</Typography>
                              <Typography variant="body2" fontWeight="medium">{ncs[0].laboratoryName}</Typography>
                            </TableCell>

                            <TableCell sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="caption" color="text.secondary">Assessment Dates</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {ncs[0].startDate} - {ncs[0].endDate}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* ASSESSOR INFO ROW 2 */}
                          <TableRow>

                            <TableCell colSpan={3} sx={{ border: '1px solid #ddd' }}>
                              <Typography variant="caption" color="text.secondary">Expertise</Typography>
                              <Typography variant="body2" fontWeight="medium">{ncs[0].expertise}</Typography>
                            </TableCell>
                          </TableRow>

                          {/* SPACER */}
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0 }}>
                              <Divider sx={{ my: 2, borderWidth: 2, borderColor: 'primary.main' }} />
                            </TableCell>
                          </TableRow>

                          {/* ALL NCs FOR THIS ASSESSOR */}
                          {ncs.map((nc, ncIndex) => (
                            <React.Fragment key={nc.id}>
                              {/* NC HEADER */}
                              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                    Non-Conformity {ncIndex + 1} of {ncs.length}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* BASIC INFO */}
                              <TableRow>

                                <TableCell sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="caption" color="text.secondary">Clause No</Typography>
                                  <Typography variant="body2" fontWeight="medium">{nc.clauseno}</Typography>
                                </TableCell>

                              </TableRow>

                              {/* NC DESCRIPTION */}
                              <TableRow>
                                <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="caption" fontWeight={600} color="error.main">
                                    NON-CONFORMITY DESCRIPTION
                                  </Typography>
                                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                    {nc.description || 'No description provided'}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* REMARKS */}
                              <TableRow>
                                <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="caption" fontWeight={600}>
                                    REMARKS
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {nc.remarks || 'No remarks'}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* CAB REPLY */}
                              <TableRow sx={{ backgroundColor: '#f9fbe7' }}>
                                <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="caption" fontWeight={600} color="primary.main">
                                    {labTypeId === '1' || labTypeId === '2' ? 'CAB Proposed Action' : 'CAB REPLY'}
                                  </Typography>
                                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                    {nc.cabReply || 'No reply provided yet'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Reply Date: {nc.cabReplyDate || 'Not available'}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              <TableRow sx={{ backgroundColor: '#f9fbe7' }}>
                                <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                  <Typography variant="caption" fontWeight={600} color="primary.main">
                                    Remark from lead assessor in case of non conformity not accepted by lab
                                  </Typography>
                                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                    {nc.assessorRemarks || 'Not available'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Reply Date: {nc.assessorRemarksDate || 'Not available'}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* NC TRAIL/HISTORY */}
                              {nc.ncreply && nc.ncreply.length > 0 && (
                                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                                  <TableCell colSpan={5} sx={{ border: '1px solid #ddd', p: 0 }}>
                                    <Accordion sx={{ boxShadow: 'none', border: 'none' }}>
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                          bgcolor: '#fff3e0',
                                          '&:hover': {
                                            bgcolor: '#ffe0b2'
                                          }
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <HistoryIcon color="warning" />
                                          <Typography variant="caption" fontWeight={600} color="warning.main">
                                            NC COMMUNICATION TRAIL ({nc.ncreply.length} entries)
                                          </Typography>
                                        </Box>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ p: 2 }}>
                                        <Stack spacing={2} sx={{ position: 'relative' }}>
                                          {nc.ncreply
                                            .sort((a, b) => new Date(a.insertdate) - new Date(b.insertdate))
                                            .map((reply, replyIndex) => (
                                              <Box
                                                key={reply.id}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'flex-start',
                                                  gap: 2,
                                                  position: 'relative'
                                                }}
                                              >
                                                {/* Timeline Dot */}
                                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                                  <Avatar
                                                    sx={{
                                                      width: 32,
                                                      height: 32,
                                                      bgcolor: reply.replyby === 'Lab' ? '#2e7d32' : '#1976d2',
                                                      border: '2px solid white',
                                                      boxShadow: 2
                                                    }}
                                                  >
                                                    {reply.replyby === 'Lab' ? (
                                                      <BusinessIcon sx={{ fontSize: 16, color: 'white' }} />
                                                    ) : (
                                                      <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                                                    )}
                                                  </Avatar>
                                                  {/* Timeline Connector */}
                                                  {replyIndex < nc.ncreply.length - 1 && (
                                                    <Box
                                                      sx={{
                                                        position: 'absolute',
                                                        left: '50%',
                                                        top: 32,
                                                        width: 2,
                                                        height: 40,
                                                        bgcolor: '#e0e0e0',
                                                        transform: 'translateX(-50%)',
                                                        zIndex: 0
                                                      }}
                                                    />
                                                  )}
                                                </Box>

                                                {/* Timeline Content */}
                                                <Box sx={{ flex: 1, pt: 0.5 }}>
                                                  <Card
                                                    elevation={1}
                                                    sx={{
                                                      p: 2,
                                                      bgcolor: reply.replyby === 'Lab' ? '#f1f8e9' : '#e3f2fd',
                                                      border: `1px solid ${reply.replyby === 'Lab' ? '#4caf50' : '#2196f3'}`
                                                    }}
                                                  >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                      <Chip
                                                        label={reply.replyby}
                                                        size="small"
                                                        color={reply.replyby === 'Lab' ? 'success' : 'primary'}
                                                        sx={{ fontWeight: 600 }}
                                                      />
                                                      <Typography variant="caption" color="text.secondary">
                                                        {new Date(reply.insertdate).toLocaleString()}
                                                      </Typography>
                                                    </Box>

                                                    {reply.replyRemarks && (
                                                      <Box sx={{ mb: 1 }}>
                                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                          Remarks:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                          {reply.replyRemarks}
                                                        </Typography>
                                                      </Box>
                                                    )}
                                                   {reply.correctiveAction && (
                                                      <Box sx={{ mb: 1 }}>
                                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                          Correction:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                          {reply.correctiveAction}
                                                        </Typography>
                                                      </Box>
                                                    )}
                                                    {reply.rootcause && (
                                                      <Box sx={{ mb: 1 }}>
                                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                          Root Cause Analysis:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                          {reply.rootcause}
                                                        </Typography>
                                                      </Box>
                                                    )}

                                                    {reply.correctiveAction && (
                                                      <Box sx={{ mb: 1 }}>
                                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                          Corrective Action:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                          {reply.correctiveAction}
                                                        </Typography>
                                                      </Box>
                                                    )}

                                                    {/* Show file uploads if any */}
                                                    {(reply.rootcauseupload || reply.correctiveActionupload) && (
                                                      <Box sx={{ mt: 1 }}>
                                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                          Attachments:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                          {reply.rootcauseupload && (
                                                            <>
                                                              {reply.rootcauseupload.split('~').filter(Boolean).map((doc, docIndex) => (
                                                                <Chip
                                                                  key={`rootcause-${docIndex}`}
                                                                  label={`Root Cause Doc ${docIndex + 1}`}
                                                                  size="small"
                                                                  onClick={() => handleFileOpen(doc, `Root Cause Document ${docIndex + 1}`)}
                                                                  clickable
                                                                  color="warning"
                                                                  sx={{ fontWeight: 600 }}
                                                                />
                                                              ))}
                                                            </>
                                                          )}
                                                          {reply.correctiveActionupload && (
                                                            <>
                                                              {reply.correctiveActionupload.split('~').filter(Boolean).map((doc, docIndex) => (
                                                                <Chip
                                                                  key={`corrective-${docIndex}`}
                                                                  label={`Corrective Action Doc ${docIndex + 1}`}
                                                                  size="small"
                                                                  onClick={() => handleFileOpen(doc, `Corrective Action Document ${docIndex + 1}`)}
                                                                  clickable
                                                                  color="success"
                                                                  sx={{ fontWeight: 600 }}
                                                                />
                                                              ))}
                                                            </>
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    )}
                                                  </Card>
                                                </Box>
                                              </Box>
                                            ))}
                                        </Stack>
                                      </AccordionDetails>
                                    </Accordion>
                                  </TableCell>
                                </TableRow>
                              )}

                              {/* DOCUMENTS */}
                              {nc.document && (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ border: '1px solid #ddd' }}>
                                    <Typography variant="caption" fontWeight={600}>
                                      SUPPORTING DOCUMENTS
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        mt: 2,
                                      }}
                                    >
                                      {nc.document
                                        .split("~")
                                        .filter(Boolean)
                                        .map((doc, docIndex) => {
                                          const docLower = doc.toLowerCase();
                                          const isImage = docLower.match(
                                            /\.(jpg|jpeg|png|gif|webp)$/
                                          );
                                          const isPdf = docLower.endsWith(".pdf");
                                          const isExcel =
                                            docLower.match(/\.(xlsx|xls)$/);

                                          return (
                                            <WorkingSpaceDocument
                                              key={docIndex}
                                              doc={doc}
                                              index={docIndex}
                                              isImage={isImage}
                                              isPdf={isPdf}
                                              isExcel={isExcel}
                                              colors={colors}
                                              onImageClick={
                                                isImage ? handleOpenLightbox : null
                                              }
                                            />
                                          );
                                        })}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}

                              {/* DIVIDER BETWEEN NCs */}
                              {ncIndex < ncs.length - 1 && (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ p: 0 }}>
                                    <Divider sx={{ my: 2 }} />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Fade>
              ))
            ) : (
              <Card elevation={3}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No Non-Conformity records found
                  </Typography>
                </Box>
              </Card>
            )}
          </Box>

          {/* End of Report */}
          <Fade in={true} timeout={3000}>
            <Paper
              elevation={4}
              sx={{
                mt: 6,
                mb: 4,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderRadius: 2,
                border: '2px solid #667eea'
              }}
            >
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                  End of Report
                </Typography>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Fade>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Lightbox for image preview */}
      {lightboxOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={handleCloseLightbox}
        >
          <Box sx={{ maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Button
            variant="contained"
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              bgcolor: 'white',
              color: 'black',
              '&:hover': { bgcolor: 'grey.300' }
            }}
            onClick={handleCloseLightbox}
          >
            Close
          </Button>
        </Box>
      )}
    </Container>
  );
};

// Working Space Document Component to handle images and PDF/Excel thumbnails
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For PDF/Excel files, show thumbnail with icon
  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? `Doc ${index + 1}` : `Doc ${index + 1}`}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
      </Box>
    );
  }

  // For images, show the actual image
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          position: "relative",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Evidence ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
          }}
        />
        {/* Document number overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            py: 0.25,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
            Doc {index + 1}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default NCMainReport;
